import type { CallLogLeg, CallLogRow, LeadCall, LeadCallState } from '../../types/leadCall.js';
import type { InboundCallOutcome, LeadInboundCall } from '../../types/speedToLead.js';
import { connectedSeconds, MAX_PLAUSIBLE_CALL_SECONDS, speedToLeadSeconds } from './callRules.js';
import { speakerLabel } from './callMedia.js';
import { LEAD_OUTCOME_LABELS } from '../../types/leadOutcome.js';
import { callResultForChain, callResultForInbound } from '../leads/callResult.js';
import type { LeadCallOutcome, LeadCallSummary } from '../../types/leadCall.js';

// THE CALL LOG PROJECTION (Stage 3C). PURE: one row per outbound chain or inbound forwarded call, in plain words.
//
// ⚠️ NOTHING IN A ROW CAN LEAK A NUMBER, A STORAGE PATH OR A VONAGE URL: legs carry a NAME and a status, the
// recording carries only its state and delete date, and playback is a separate signed 5-minute URL. The portal
// uses `forClub: true` to drop the Kyck-only detail (end reasons that name internal machinery).

const OUTBOUND_LABEL: Record<LeadCallState, string> = {
  scheduled: 'Scheduled for the next opening',
  deferred: 'Waiting: the line was busy',
  staff_ringing: 'Ringing the team',
  between_rounds: 'Between rounds',
  retry_scheduled: 'Retry scheduled',
  lead_dialling: 'Calling the lead',
  bridged: 'On a call now',
  ended: 'Ended',
  missed_all: 'Nobody on the team answered',
  lead_unreachable: 'The lead did not answer',
  missed_out_of_hours: 'Missed: out of hours',
  capped: 'Not placed: call limit reached',
  failed_system: 'Failed: system error',
  cancelled: 'Cancelled',
};

const INBOUND_LABEL: Record<InboundCallOutcome, string> = {
  answered_primary: 'Answered by the club',
  answered_staff: 'Answered by staff',
  no_answer: 'Nobody answered',
  out_of_hours: 'Out of hours',
  disabled: 'Forwarding is off',
  unrouted: 'Not routed to a club',
};

/**
 * STAGE 4.5: the CALL RESULT is the label (one automatic label per call: Connected, Club didn't pick up, Lead didn't pick up,
 * Screener or voicemail ended, Out of hours, Not called). Only a call still in progress has no result yet, and it reads its
 * state (Ringing the team, Calling the lead, ...).
 */
export function outboundOutcomeLabel(c: Pick<LeadCall, 'state' | 'leadReachedAt' | 'screenedOrMachine'>): string {
  return callResultForChain(c)?.label ?? OUTBOUND_LABEL[c.state];
}
export const inboundOutcomeLabel = (c: Pick<LeadInboundCall, 'outcome' | 'answeredAt' | 'endedAt'>): string => callResultForInbound(c)?.label ?? INBOUND_LABEL[c.outcome];

const recordingOf = (r: LeadCall['recording']): CallLogRow['recording'] => (r ? { status: r.status, deleteAfter: r.deleteAfter } : null);
const transcriptOf = (t: LeadCall['transcript'], kind: 'outbound' | 'inbound', names: Map<string, string>, clubName: string | null): CallLogRow['transcript'] =>
  t
    ? {
        status: t.status,
        text: t.text,
        segments: t.segments ? t.segments.map((s) => ({ ...s, label: speakerLabel(s.speaker, kind, s.staffMemberId ? (names.get(s.staffMemberId) ?? null) : null, clubName) })) : null,
        speakerLabels: t.speakerLabels ?? 'unavailable',
        speakerLabelSource: t.speakerLabelSource ?? null,
        reason: t.reason,
      }
    : null;

const summaryOf = (s: LeadCallSummary | null | undefined): CallLogRow['summary'] =>
  s ? { status: s.status, text: s.status === 'ready' ? s.text : null, suggestedOutcome: s.status === 'ready' ? s.suggestedOutcome : null, confidence: s.status === 'ready' ? s.confidence : null, nextStep: s.status === 'ready' ? s.nextStep : null } : null;
const outcomeOf = (o: LeadCallOutcome | null | undefined): CallLogRow['outcomeRecord'] =>
  o ? { outcome: o.outcome, label: LEAD_OUTCOME_LABELS[o.outcome], source: o.source, confirmedByName: o.confirmedByName, confirmedAt: o.confirmedAt } : null;

export function projectOutbound(c: LeadCall, opts: { forClub?: boolean; clubName?: string | null } = {}): CallLogRow {
  const names = new Map(c.staffPlan.map((s) => [s.teamMemberId, s.name]));
  const legs: CallLogLeg[] = c.legs.map((l) => ({
    role: l.role,
    who: l.role === 'lead' ? 'The lead' : ((l.who ? names.get(l.who) : null) ?? 'Team member'),
    status: l.status,
    startedAt: l.startedAt ?? null,
    answeredAt: l.answeredAt,
    endedAt: l.endedAt,
    reason: opts.forClub ? null : l.reason,
  }));
  return {
    kind: 'outbound',
    id: c.id,
    at: c.leadReceivedAt,
    outcome: outboundOutcomeLabel(c),
    leadName: c.leadName,
    source: c.source,
    ...(c.isTest ? { isTest: true } : {}),
    answeredBy: c.answeredBy ? (names.get(c.answeredBy) ?? 'Team member') : null,
    durationSeconds: connectedSeconds(c),
    speedToLeadSeconds: speedToLeadSeconds(c),
    ...(c.kind === 'manual' && c.manualBy ? { manualBy: opts.forClub && c.manualBy.surface === 'kyck' ? 'Kyck' : (c.manualBy.name.trim().split(/\s+/)[0] || 'Someone') } : {}),
    endReason: opts.forClub ? null : c.endReason,
    samFallback: c.samFallback ?? null,
    legs,
    recording: recordingOf(c.recording),
    recordingOffsetSeconds: c.recordingOffsetSeconds ?? null,
    transcript: transcriptOf(c.transcript, 'outbound', names, opts.clubName ?? null),
    summary: summaryOf(c.summary),
    outcomeRecord: outcomeOf(c.callOutcome),
  };
}

export function projectInbound(c: LeadInboundCall, names: Map<string, string>, clubName: string | null = null): CallLogRow {
  const legs: CallLogLeg[] = c.legs.map((l, i) => {
    const decision = c.legDecisions?.[String(i)];
    return {
      role: l.kind,
      who: l.kind === 'primary' ? 'The club line' : l.kind === 'group_staff' ? 'Group staff' : ((l.teamMemberId ? names.get(l.teamMemberId) : null) ?? 'Team member'),
      status: decision ? decision.result : 'rang',
      startedAt: null,
      answeredAt: decision?.result === 'accepted' ? c.answeredAt : null,
      endedAt: null,
      reason: null,
    };
  });
  const answeredBy =
    c.answeredBy === 'primary' ? 'The club line' : c.answeredBy === 'group_staff' ? 'Group staff' : c.answeredBy ? (names.get(c.answeredBy) ?? 'Team member') : null;
  return {
    kind: 'inbound',
    id: c.callUuid,
    at: c.receivedAt,
    outcome: inboundOutcomeLabel(c),
    leadName: null,
    source: null,
    answeredBy,
    durationSeconds: c.durationSeconds !== null && c.durationSeconds >= 0 && c.durationSeconds <= MAX_PLAUSIBLE_CALL_SECONDS ? c.durationSeconds : null,
    speedToLeadSeconds: null,
    endReason: null,
    legs,
    recording: recordingOf(c.recording),
    transcript: transcriptOf(c.transcript, 'inbound', names, clubName),
    summary: summaryOf(c.summary),
    outcomeRecord: outcomeOf(c.callOutcome),
  };
}

/** Newest first. */
export const sortCallLog = (rows: CallLogRow[]): CallLogRow[] => [...rows].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

/**
 * The one note under a transcript about who spoke. `labelled` by channels/audio is a measurement and carries no note; `ai` is an
 * INFERENCE and says so; unlabelled says the speakers could not be told apart. ⚠️ AI labels are never shown as measured.
 */
export function speakerNote(t: { speakerLabels: 'labelled' | 'unavailable'; speakerLabelSource?: string | null } | null | undefined): string | null {
  if (!t) return null;
  if (t.speakerLabels !== 'labelled') return 'Speakers could not be told apart for this call.';
  return t.speakerLabelSource === 'ai' ? 'Speakers identified by AI.' : null;
}
